import { useEffect, useMemo, useRef, useState } from "react";
import ReactGridLayout, { noCompactor, type Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { stackOrder, type LayoutBody, type WidgetInstance } from "../../shared/layout";
import { getWidget } from "../widgets/registry";
import { useDevicePrefs } from "../app/devicePrefs";
import { resolveConfig, stackHeightOf } from "../widgets/types";
import { WidgetShell } from "../widgets/WidgetShell";

/** Below this the wall grid would be too cramped; widgets stack in a scrolling column instead. */
const MIN_WALL_WIDTH = 900;
const MIN_WALL_HEIGHT = 480;

// Collisions are refused rather than pushing other widgets off the fitted wall.
const compactor = { ...noCompactor, preventCollision: true };

function useBoxSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const r = entry!.contentRect;
      setSize((s) => (Math.abs(s.width - r.width) < 1 && Math.abs(s.height - r.height) < 1 ? s : { width: r.width, height: r.height }));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return { ref, ...size };
}

export function Dashboard({
  body,
  editing,
  onChange,
  onConfigure,
  onRemove,
}: {
  body: LayoutBody;
  editing: boolean;
  onChange: (items: WidgetInstance[]) => void;
  onConfigure: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const box = useBoxSize<HTMLDivElement>();
  const compact = useDevicePrefs().density === "compact";
  const wall = box.width >= MIN_WALL_WIDTH && box.height >= MIN_WALL_HEIGHT;
  const { cols, rows } = body.grid;
  // Gutters scale with the screen: ~14px on a 1080p wall, 9px on small screens; tighter when compact.
  const MARGIN = Math.round(Math.min(16, Math.max(9, box.width * 0.0075)) * (compact ? 0.65 : 1));
  const PAD = MARGIN;
  const rowHeight = Math.max(40, (box.height - PAD * 2 - MARGIN * (rows - 1)) / rows);
  const colWidth = (box.width - PAD * 2 - MARGIN * (cols - 1)) / cols;

  const rglLayout: Layout = useMemo(
    () =>
      body.items.map((it) => {
        const def = getWidget(it.type);
        return {
          i: it.id,
          ...it.wall,
          minW: def?.minSize.w ?? 1,
          minH: def?.minSize.h ?? 1,
          maxW: def?.maxSize?.w,
          maxH: def?.maxSize?.h,
        };
      }),
    [body.items],
  );

  const commit = (layout: Layout) => {
    const pos = new Map(layout.map((l) => [l.i, l]));
    onChange(
      body.items.map((it) => {
        const l = pos.get(it.id);
        return l ? { ...it, wall: { x: l.x, y: l.y, w: l.w, h: l.h } } : it;
      }),
    );
  };

  if (box.width === 0) return <div ref={box.ref} className="dashboard" />;

  if (!wall) {
    const order = stackOrder(body.items);
    // Phones list widgets in wall order, so moving one swaps its wall position
    // with its neighbour's: the wall stays overlap-free and the order matches.
    const swap = (i: number, j: number) => {
      const a = order[i]!;
      const b = order[j]!;
      onChange(body.items.map((it) => (it.id === a.id ? { ...it, wall: b.wall } : it.id === b.id ? { ...it, wall: a.wall } : it)));
    };
    return (
      <div ref={box.ref} className="dashboard dashboard-stack">
        {editing && <p className="stack-note">Use the arrows to reorder. Moving a widget here also swaps its place on the wall; resize on a larger screen.</p>}
        {order.map((it, i) => {
          const def = getWidget(it.type);
          const h = def ? stackHeightOf(def, resolveConfig(def, it.config, it.version)) : 300;
          return (
            <div key={it.id} className="stack-item" style={{ height: h }}>
              <WidgetShell
                item={it}
                width={box.width - PAD * 2}
                height={h}
                editing={editing}
                onConfigure={() => onConfigure(it.id)}
                onRemove={() => onRemove(it.id)}
                onMove={{ up: i > 0 ? () => swap(i, i - 1) : null, down: i < order.length - 1 ? () => swap(i, i + 1) : null }}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={box.ref} className={`dashboard dashboard-wall ${editing ? "is-editing" : ""}`}>
      {editing && <GridGuides cols={cols} rows={rows} colWidth={colWidth} rowHeight={rowHeight} gap={MARGIN} />}
      <ReactGridLayout
        layout={rglLayout}
        width={box.width}
        gridConfig={{ cols, rowHeight, margin: [MARGIN, MARGIN], containerPadding: [PAD, PAD], maxRows: rows }}
        dragConfig={{ enabled: editing, handle: ".km-drag-handle", bounded: true, threshold: 6 }}
        resizeConfig={{ enabled: editing, handles: ["se"] }}
        compactor={compactor}
        autoSize={false}
        style={{ height: box.height }}
        onDragStop={(layout) => commit(layout)}
        onResizeStop={(layout) => commit(layout)}
      >
        {body.items.map((it) => {
          const width = it.wall.w * colWidth + (it.wall.w - 1) * MARGIN;
          const height = it.wall.h * rowHeight + (it.wall.h - 1) * MARGIN;
          return (
            <div key={it.id} className="grid-cell">
              <WidgetShell item={it} width={width} height={height} editing={editing} onConfigure={() => onConfigure(it.id)} onRemove={() => onRemove(it.id)} />
            </div>
          );
        })}
      </ReactGridLayout>
    </div>
  );
}

/** Faint snap-grid shown behind widgets while editing. */
function GridGuides({ cols, rows, colWidth, rowHeight, gap }: { cols: number; rows: number; colWidth: number; rowHeight: number; gap: number }) {
  return (
    <div
      className="grid-guides"
      style={{
        gridTemplateColumns: `repeat(${cols}, ${colWidth}px)`,
        gridTemplateRows: `repeat(${rows}, ${rowHeight}px)`,
        gap,
        padding: gap,
      }}
      aria-hidden="true"
    >
      {Array.from({ length: cols * rows }, (_, i) => (
        <i key={i} />
      ))}
    </div>
  );
}
